<template>
  <div v-if="zone" class="modal-content-frame modal-content-frame-sensors">
    <div class="zone-go-back" @click="goToStageOne">
      <IconGoBack />
      Go Back
    </div>

    <div class="zone-header zone-header--sensors">
      <div>
        <label for="chips">Add Sensor</label>
        <p>Select one sensor to add. Greyed out sensors are already assigned to zones.</p>
      </div>
    </div>

    <div class="event-form--row">
      <div class="table-filters table-filters--search">
        <InputText
          v-model="sensorParams.search"
          name="search"
          label="Search"
          :hasIconLeft="true"
          @onBlur="searchChanged"
          @onEnter="searchChanged"
        >
          <template #icon>
            <IconSearch />
          </template>
        </InputText>
        <Button @click="searchChanged">Search</Button>
      </div>

      <table>
        <TableHeader
          :columns="columns"
          :sortBy="sensorParams.sort"
          :sortDesc="sensorParams.sortDesc"
          :hasFirstCol="true"
          @colSort="colSort"
        >
          <template #firstCol></template>
        </TableHeader>

        <tbody v-if="loading">
          <tr>
            <td colspan="4">
              <LoadingModal />
            </td>
          </tr>
        </tbody>

        <tbody v-if="!loading" class="zone-sensors">
          <tr v-if="sensorsData.length === 0">
            <td :colspan="columns.length" class="col-center">
              No results found for these parameters.
            </td>
          </tr>
          <tr
            v-for="sensor in sensorsData"
            :key="sensor.id"
            :class="{
              'used-sensor':
                sensor.id &&
                (sensorsInOtherZones.includes(sensor?.id) || sensor.available === false),
            }"
          >
            <td class="col-center" v-if="sensor.id">
              <Tooltip
                position="right"
                :hasTooltip="sensorsInOtherZones.includes(sensor?.id) || sensor.available === false"
              >
                <template v-slot:text>
                  <Checkbox
                    v-model="zone.sensors"
                    :value="sensor"
                    :disabled="
                      sensorsInOtherZones.includes(sensor?.id) || sensor.available === false
                    "
                    @change="changeSensor(sensor)"
                  />
                </template>
                <template v-slot:tooltip>
                  <template v-if="sensorsInOtherZones.includes(sensor?.id)"
                    >This sensor is in use</template
                  >
                  <template v-else-if="sensor.available === false"
                    >This sensor is unavailable</template
                  >
                </template>
              </Tooltip>
            </td>
            <td>{{ sensor.name }}</td>
            <td>{{ sensor.mac_address }}</td>
            <td class="col-center">
              <SensorStatus :online="sensor.is_up" />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="table-footer">
        <Pagination
          :currentPage="sensorParams.page"
          :totalPages="lastPage"
          @pageChanged="pageChanged"
        />

        <PerPage v-model="sensorParams.results_per_page" @changedResultsPerPage="perPageChanged" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { Ref } from 'vue';
import dayjs from 'dayjs';

import { getSensors } from '@/utils/api';

import type { Column } from '@/types/Column';
import type { SensorType, SensorIndexQuery } from '@/types/Sensor';
import type { EventTypeNew, ZoneType } from '@/types/Event';

import InputText from '@/components/inputs/InputText.vue';
import { Button, Checkbox } from 'primevue';

import LoadingModal from '@/components/global/LoadingModal.vue';
import Tooltip from '@/components/global/Tooltip.vue';
import Pagination from '@/components/tables/table-controls/Pagination.vue';
import PerPage from '@/components/tables/table-controls/PerPage.vue';
import SensorStatus from '@/components/sensors/SensorStatus.vue';
import TableHeader from '@/components/tables/TableHeader.vue';

import IconGoBack from '@/icons/events/IconGoBack.svg?component';
import IconSearch from '@/icons/IconSearch.svg?component';

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

const newEvent = defineModel<EventTypeNew>('newEvent');
const zone = defineModel<ZoneType>('zone');

const sensorsData = ref<SensorType[]>([]);

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    defaultSensors?: SensorType[];
    index: number;
  }>(),
  {
    disabled: false,
  },
);

const sensorsInOtherZones = computed(() => {
  // Trick it into ignoring itself when making the comparison
  if (newEvent.value?.zones) {
    const zones = [...newEvent.value.zones];
    const result: number[] = [];
    return zones.reduce((results: number[], zone: any) => {
      return [...results, ...zone.sensors.map((sensor: SensorType) => sensor.id)];
    }, result);
  }
  return [];
});

const columns = ref<Column[]>([
  {
    id: 'check',
    name: '',
    sortable: false,
  },
  {
    id: 'name',
    name: 'Name',
    sortable: true,
  },
  {
    id: 'mac_address',
    name: 'Mac ID',
    sortable: true,
  },
  {
    id: 'is_up',
    name: 'Status',
    sortable: true,
  },
]);

const colSort = (field: string) => {
  sensorParams.value.page = 1;

  if (sensorParams.value.sort === field) {
    sensorParams.value.sortDesc = !sensorParams.value.sortDesc;
  } else {
    sensorParams.value.sort = field;
    sensorParams.value.sortDesc = false;
  }

  getSensorData();
};

const searchChanged = () => {
  sensorParams.value.page = 1;
  getSensorData();
};

const perPageChanged = () => {
  sensorParams.value.page = 1;
  getSensorData();
};

const pageChanged = (page: number) => {
  sensorParams.value.page = page;
  getSensorData();
};

const loading = ref(false);

const lastPage = ref(0);
const sensorParams: Ref<SensorIndexQuery> = ref({
  page: 1,
  results_per_page: 10,
  search: '',
  sortDesc: false,
  sort: 'name',
});

const getSensorData = async () => {
  loading.value = true;

  const newParams = { ...sensorParams.value };
  if (newEvent.value?.account_id) {
    newParams.account = newEvent.value.account_id;
  }
  if (newEvent.value?.event_start) {
    newParams.start_date = dayjs(newEvent.value.event_start).format('YYYY-MM-DD HH:mm');
  }
  if (newEvent.value?.event_end) {
    newParams.end_date = dayjs(newEvent.value.event_end).format('YYYY-MM-DD HH:mm');
  }
  if (newEvent.value?.timezone_id) {
    newParams.location_id = newEvent.value.timezone_id;
  }

  newParams.sortDesc = newParams.sortDesc === true ? 1 : 0;
  newParams.include_inactive = newParams.include_inactive === true ? 1 : 0;

  getSensors(userStore.accessToken, newParams).then((response) => {
    const { data, last_page } = response;
    sensorsData.value = data;

    lastPage.value = last_page;
    loading.value = false;
  });
};

getSensorData();

const changeSensor = (sensor: SensorType) => {
  if (zone.value) {
    zone.value.sensors = [];
    zone.value.sensors.push(sensor);
  }
};

const emit = defineEmits(['changeStage']);

const goToStageOne = () => {
  emit('changeStage');
};
</script>
